/**
 * useBrandProfile — loads/saves the user's AI Brand Profile.
 *
 * Wrap the AI CPG Tools routes in <BrandProfileProvider>. Source of truth is the backend
 * (GET/PUT /api/ai-lab/brand-profile); localStorage is a cache + offline fallback so
 * "Use my brand profile" works instantly and keeps working if the API is unreachable.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { brandProfileApi } from "@/lib/ai-lab/api";
import { EMPTY_BRAND_PROFILE, brandProfileCompleteness } from "@/data/ai-lab/brandProfileSchema";

const STORAGE_KEY = "cpg-ai-lab:brand-profile";
const BrandProfileContext = createContext(null);

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(profile) {
  try {
    if (profile) localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

export function BrandProfileProvider({ children }) {
  const [profile, setProfile] = useState(() => readCache());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    brandProfileApi
      .get()
      .then((data) => {
        if (cancelled) return;
        if (data && data.brandName !== undefined) {
          setProfile(data);
          writeCache(data);
        }
      })
      .catch(() => {
        /* keep cached copy */
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async (next) => {
    const merged = { ...EMPTY_BRAND_PROFILE, ...(profile || {}), ...next };
    setSaving(true);
    setError(null);
    setProfile(merged);
    writeCache(merged);
    try {
      const saved = await brandProfileApi.save(merged);
      if (saved && saved.brandName !== undefined) {
        setProfile(saved);
        writeCache(saved);
      }
      return saved || merged;
    } catch (e) {
      setError("Saved on this device, but we couldn't reach the server. It will sync next time.");
      return merged;
    } finally {
      setSaving(false);
    }
  }, [profile]);

  const clear = useCallback(async () => {
    setProfile(null);
    writeCache(null);
    try {
      await brandProfileApi.remove();
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(() => {
    const completeness = brandProfileCompleteness(profile);
    return {
      profile,
      hasProfile: !!profile && !!profile.brandName,
      completeness,
      loading,
      saving,
      error,
      save,
      clear,
    };
  }, [profile, loading, saving, error, save, clear]);

  return <BrandProfileContext.Provider value={value}>{children}</BrandProfileContext.Provider>;
}

export function useBrandProfile() {
  const ctx = useContext(BrandProfileContext);
  if (!ctx) {
    // Allow components to render outside the provider (e.g. in storybook) with a safe default.
    return { profile: null, hasProfile: false, completeness: 0, loading: false, saving: false, error: null, save: async () => null, clear: async () => {} };
  }
  return ctx;
}
